import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useScrollLock } from '../hooks/useScrollLock';
import { useSheetDrag } from '../hooks/useSheetDrag';
import { getCoachAthletesWithStats, addAthleteToCoach, getCoachPrograms, createProgram, reassignAthleteProgram, unassignAthleteProgram, duplicateProgram, updateProgram, deleteProgram, getProgramActiveAssignments } from '../lib/database';
import SkeletonLoader from './SkeletonLoader';
import EmptyState from './EmptyState';
import './CoachDashboard.css';

function CoachDashboard() {
  const navigate = useNavigate();
  const { signOut, profile } = useAuth();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('athletes');
  const [athletes, setAthletes] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showProgramModal, setShowProgramModal] = useState(false);
  const [showAssignProgramModal, setShowAssignProgramModal] = useState(false);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [confirmUnassign, setConfirmUnassign] = useState(null);
  const [programActions, setProgramActions] = useState(null); // program object for actions sheet
  const [showRenameSheet, setShowRenameSheet] = useState(false);
  const [renamingProgram, setRenamingProgram] = useState(null);
  const [confirmDeleteProgram, setConfirmDeleteProgram] = useState(null);
  const [deleteActiveCount, setDeleteActiveCount] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (profile?.id) {
      loadData();
    }
  }, [profile]);

  const loadData = async () => {
    await Promise.all([loadAthletes(), loadPrograms()]);
  };

  const loadAthletes = async () => {
    try {
      setLoading(true);
      // Usa la funzione batch ottimizzata che riduce le chiamate DB da ~60 a ~5
      const athletesWithData = await getCoachAthletesWithStats(profile.id);
      setAthletes(athletesWithData);
    } catch (error) {
      console.error('Error loading athletes:', error);
      setAthletes([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPrograms = async () => {
    try {
      const data = await getCoachPrograms(profile.id);
      setPrograms(data);
    } catch (error) {
      console.error('Error loading programs:', error);
      setPrograms([]);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/', { replace: true });
    }
  };

  const handleAddAthleteSubmit = async (email) => {
    const res = await addAthleteToCoach(profile.id, email);
    await loadAthletes();
    if (res?.alreadyLinked) {
      toast.info('Atleta già presente');
    } else {
      toast.success('Atleta aggiunto');
    }
  };

  const handleCreateProgramSubmit = async (programData) => {
    try {
      await createProgram(profile.id, programData);
      await loadPrograms();
      setShowProgramModal(false);
      toast.success('Programma creato con successo');
    } catch (error) {
      console.error('Error creating program:', error);
      toast.error('Errore nella creazione del programma');
    }
  };

  const formatRelativeTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Oggi';
    if (diffDays === 1) return 'Ieri';
    if (diffDays < 7) return `${diffDays} giorni fa`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} sett. fa`;
    return `${Math.floor(diffDays / 30)} mesi fa`;
  };

  const handleAssignProgram = async (programId) => {
    try {
      await reassignAthleteProgram(selectedAthlete.athleteId, programId);
      await loadAthletes();
      setShowAssignProgramModal(false);
      setSelectedAthlete(null);
      toast.success('Programma assegnato con successo');
    } catch (error) {
      console.error('Error assigning program:', error);
      toast.error('Errore nell\'assegnazione del programma');
    }
  };

  const handleUnassignProgram = async (e, athleteId) => {
    e.stopPropagation();
    try {
      await unassignAthleteProgram(athleteId);
      await loadAthletes();
      setConfirmUnassign(null);
      toast.success('Programma disassegnato');
    } catch (error) {
      console.error('Error unassigning program:', error);
      toast.error('Errore nella disassegnazione del programma');
    }
  };

  // --- Program action handlers ---
  const handleDuplicateProgram = async (program) => {
    setActionLoading(true);
    try {
      await duplicateProgram(program.id, profile.id, 'coach');
      await loadPrograms();
      setProgramActions(null);
      toast.success('Programma duplicato con successo');
    } catch (error) {
      console.error('Error duplicating program:', error);
      toast.error('Errore nella duplicazione del programma');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRenameProgram = async (programId, newName) => {
    setActionLoading(true);
    try {
      await updateProgram(programId, { name: newName });
      await loadPrograms();
      setShowRenameSheet(false);
      setRenamingProgram(null);
      toast.success('Programma rinominato');
    } catch (error) {
      console.error('Error renaming program:', error);
      toast.error('Errore nella rinomina del programma');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteProgramClick = async (program) => {
    const activeCount = await getProgramActiveAssignments(program.id);
    setDeleteActiveCount(activeCount);
    setConfirmDeleteProgram(program);
    setProgramActions(null);
  };

  const handleDeleteProgramConfirm = async () => {
    if (!confirmDeleteProgram) return;
    setActionLoading(true);
    try {
      await deleteProgram(confirmDeleteProgram.id);
      await Promise.all([loadPrograms(), loadAthletes()]);
      setConfirmDeleteProgram(null);
      toast.success('Programma eliminato');
    } catch (error) {
      console.error('Error deleting program:', error);
      toast.error('Errore nell\'eliminazione del programma');
    } finally {
      setActionLoading(false);
    }
  };

  // Filter athletes by search
  const filteredAthletes = athletes.filter(a =>
    a.name.toLowerCase().includes(search.toLowerCase())
  );

  // Loading skeleton
  if (loading) {
    return (
      <div className="cd-container">
        <header className="cd-header">
          <div className="cd-header-top">
            <span className="cd-header-title">Uptra</span>
            <button className="cd-btn-logout" onClick={handleLogout}>Esci</button>
          </div>
          <div className="cd-tabs">
            <button className="cd-tab cd-tab--active">Atleti</button>
            <button className="cd-tab">Programmi</button>
          </div>
        </header>
        <main className="cd-main">
          <SkeletonLoader variant="list" count={5} />
        </main>
      </div>
    );
  }

  return (
    <div className="cd-container">
      {/* Sticky Header */}
      <header className="cd-header">
        <div className="cd-header-top">
          <button
            className="cd-btn-logout"
            onClick={() => navigate('/athlete')}
            aria-label="Torna all'app"
            style={{ background: 'transparent' }}
          >
            ← App
          </button>
          <span className="cd-header-title">Coach</span>
          <button className="cd-btn-logout" onClick={handleLogout}>Esci</button>
        </div>
        <div className="cd-tabs">
          <button
            className={`cd-tab ${activeTab === 'athletes' ? 'cd-tab--active' : ''}`}
            onClick={() => setActiveTab('athletes')}
          >
            Atleti
          </button>
          <button
            className={`cd-tab ${activeTab === 'programs' ? 'cd-tab--active' : ''}`}
            onClick={() => setActiveTab('programs')}
          >
            Programmi
          </button>
        </div>
      </header>

      <main className="cd-main">
        {activeTab === 'athletes' ? (
          <>
            {/* Search */}
            {athletes.length > 0 && (
              <input
                className="cd-search"
                type="text"
                placeholder="Cerca atleta..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            )}

            {athletes.length === 0 ? (
              <EmptyState
                icon="👥"
                title="Nessun atleta ancora"
                description="Inizia ad aggiungere i tuoi atleti per monitorare i loro progressi e assegnare programmi di allenamento."
                actionLabel="Aggiungi primo atleta"
                onAction={() => setShowAddModal(true)}
              />
            ) : filteredAthletes.length === 0 ? (
              <EmptyState
                icon="🔍"
                title="Nessun risultato"
                description="Nessun atleta corrisponde alla tua ricerca. Prova con un altro nome."
              />
            ) : (
              <div className="cd-list">
                {filteredAthletes.map(athlete => (
                  <div
                    key={athlete.id}
                    className="cd-athlete"
                    onClick={() => navigate(`/coach/athlete/${athlete.athleteId}`)}
                  >
                    <h3 className="cd-athlete-name">{athlete.name}</h3>

                    <div className="cd-athlete-program">
                      {athlete.program ? (
                        <span className="cd-badge-program">{athlete.program}</span>
                      ) : (
                        <span className="cd-badge-no-program">Nessuna scheda</span>
                      )}
                    </div>

                    <div className="cd-athlete-meta">
                      <strong>{athlete.stats.totalWorkouts}</strong> allenamenti
                      {athlete.stats.streak > 0 && (
                        <> · 🔥 {athlete.stats.streak} giorni</>
                      )}
                      {athlete.lastWorkout && (
                        <> · Ultimo: {formatRelativeTime(athlete.lastWorkout.completed_at)}</>
                      )}
                    </div>

                    <div className="cd-actions">
                      <button
                        className="cd-btn cd-btn--secondary"
                        onClick={(e) => { e.stopPropagation(); navigate(`/coach/athlete/${athlete.athleteId}`); }}
                      >
                        Dettagli
                      </button>
                      <button
                        className="cd-btn cd-btn--outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedAthlete(athlete);
                          setShowAssignProgramModal(true);
                        }}
                      >
                        {athlete.program ? 'Cambia scheda' : 'Assegna scheda'}
                      </button>
                      {athlete.program && (
                        confirmUnassign === athlete.athleteId ? (
                          <div className="cd-unassign-confirm">
                            <span className="cd-unassign-text">Rimuovere?</span>
                            <button
                              className="cd-btn--confirm-yes"
                              onClick={(e) => handleUnassignProgram(e, athlete.athleteId)}
                            >
                              Sì
                            </button>
                            <button
                              className="cd-btn--confirm-no"
                              onClick={(e) => { e.stopPropagation(); setConfirmUnassign(null); }}
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <button
                            className="cd-btn cd-btn--danger-ghost"
                            onClick={(e) => { e.stopPropagation(); setConfirmUnassign(athlete.athleteId); }}
                          >
                            Rimuovi
                          </button>
                        )
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <button className="cd-fab" onClick={() => setShowAddModal(true)}>+</button>
          </>
        ) : (
          <>
            {programs.length === 0 ? (
              <EmptyState
                icon="📋"
                title="Nessun programma ancora"
                description="Crea il tuo primo programma di allenamento per poterlo assegnare ai tuoi atleti."
                actionLabel="Crea primo programma"
                onAction={() => setShowProgramModal(true)}
              />
            ) : (
              <div className="cd-list">
                {programs.map(program => (
                  <div
                    key={program.id}
                    className="cd-program"
                    onClick={() => navigate(`/program/${program.id}`)}
                  >
                    <div className="cd-program-header">
                      <h3 className="cd-program-name">{program.name}</h3>
                      <button
                        className="cd-program-menu"
                        onClick={(e) => { e.stopPropagation(); setProgramActions(program); }}
                        aria-label="Azioni programma"
                      >
                        ⋯
                      </button>
                    </div>
                    <p className="cd-program-desc">
                      {program.description || 'Nessuna descrizione'}
                    </p>
                  </div>
                ))}
              </div>
            )}

            <button className="cd-fab" onClick={() => setShowProgramModal(true)}>+</button>
          </>
        )}
      </main>

      {/* Bottom Sheet: Add Athlete */}
      <AddAthleteSheet
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddAthleteSubmit}
      />

      {/* Bottom Sheet: Create Program */}
      <CreateProgramSheet
        isOpen={showProgramModal}
        onClose={() => setShowProgramModal(false)}
        onCreate={handleCreateProgramSubmit}
      />

      {/* Bottom Sheet: Assign Program */}
      <AssignProgramSheet
        isOpen={showAssignProgramModal}
        onClose={() => {
          setShowAssignProgramModal(false);
          setSelectedAthlete(null);
        }}
        athlete={selectedAthlete}
        programs={programs}
        onAssign={handleAssignProgram}
      />

      {/* Bottom Sheet: Program Actions */}
      <ProgramActionsSheet
        isOpen={!!programActions}
        program={programActions}
        loading={actionLoading}
        onClose={() => setProgramActions(null)}
        onEdit={() => { navigate(`/program/${programActions?.id}`); setProgramActions(null); }}
        onDuplicate={() => programActions && handleDuplicateProgram(programActions)}
        onRename={() => {
          setRenamingProgram(programActions);
          setProgramActions(null);
          setShowRenameSheet(true);
        }}
        onDelete={() => programActions && handleDeleteProgramClick(programActions)}
      />

      {/* Bottom Sheet: Rename Program */}
      <RenameProgramSheet
        isOpen={showRenameSheet}
        program={renamingProgram}
        loading={actionLoading}
        onClose={() => { setShowRenameSheet(false); setRenamingProgram(null); }}
        onRename={handleRenameProgram}
      />

      {/* Delete Confirmation */}
      {confirmDeleteProgram && (
        <DeleteConfirmSheet
          program={confirmDeleteProgram}
          activeCount={deleteActiveCount}
          loading={actionLoading}
          onClose={() => setConfirmDeleteProgram(null)}
          onConfirm={handleDeleteProgramConfirm}
        />
      )}
    </div>
  );
}

/* ===== Bottom Sheet: Add Athlete ===== */
function AddAthleteSheet({ isOpen, onClose, onAdd }) {
  useScrollLock(isOpen);
  const { contentRef, sheetStyle, overlayOpacity, handleTouchStart: onTS, handleTouchMove: onTM, handleTouchEnd: onTE, handleClose } = useSheetDrag(onClose);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await onAdd(email);
      setEmail('');
      onClose();
    } catch (err) {
      setError(err.message || 'Errore durante l\'aggiunta dell\'atleta');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="cd-sheet-overlay" style={{ backgroundColor: `rgba(0,0,0,${overlayOpacity})` }} onClick={handleClose}>
      <div ref={contentRef} className="cd-sheet" style={sheetStyle} onClick={(e) => e.stopPropagation()}
           onTouchStart={onTS} onTouchMove={onTM} onTouchEnd={onTE}>
        <div className="cd-sheet-handle" />
        <div className="cd-sheet-header">
          <h2 className="cd-sheet-title">Aggiungi Atleta</h2>
          <button className="cd-sheet-close" onClick={handleClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit} className="cd-sheet-body">
          <div className="cd-field">
            <label>Email atleta</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="esempio@email.com"
              required
              disabled={loading}
            />
            <span className="cd-field-hint">
              L'atleta deve avere un account registrato come "Atleta"
            </span>
          </div>

          {error && <div className="cd-field-error">{error}</div>}

          <div className="cd-sheet-actions">
            <button
              type="button"
              className="cd-sheet-btn-cancel"
              onClick={handleClose}
              disabled={loading}
            >
              Annulla
            </button>
            <button
              type="submit"
              className="cd-sheet-btn-submit"
              disabled={loading}
            >
              {loading ? 'Aggiunta...' : 'Aggiungi'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ===== Bottom Sheet: Create Program ===== */
function CreateProgramSheet({ isOpen, onClose, onCreate }) {
  useScrollLock(isOpen);
  const { contentRef, sheetStyle, overlayOpacity, handleTouchStart: onTS, handleTouchMove: onTM, handleTouchEnd: onTE, handleClose } = useSheetDrag(onClose);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (name.trim()) {
      onCreate({ name, description });
      setName('');
      setDescription('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="cd-sheet-overlay" style={{ backgroundColor: `rgba(0,0,0,${overlayOpacity})` }} onClick={handleClose}>
      <div ref={contentRef} className="cd-sheet" style={sheetStyle} onClick={(e) => e.stopPropagation()}
           onTouchStart={onTS} onTouchMove={onTM} onTouchEnd={onTE}>
        <div className="cd-sheet-handle" />
        <div className="cd-sheet-header">
          <h2 className="cd-sheet-title">Nuovo Programma</h2>
          <button className="cd-sheet-close" onClick={handleClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit} className="cd-sheet-body">
          <div className="cd-field">
            <label>Nome Programma</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="es. Ipertrofia Gennaio"
              required
            />
          </div>
          <div className="cd-field">
            <label>Descrizione</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descrizione del programma..."
              rows="3"
            />
            <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>Non inserire informazioni mediche, diagnosi o dati sanitari.</p>
          </div>
          <div className="cd-sheet-actions">
            <button type="button" className="cd-sheet-btn-cancel" onClick={handleClose}>
              Annulla
            </button>
            <button type="submit" className="cd-sheet-btn-submit" disabled={!name.trim()}>
              Crea
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ===== Bottom Sheet: Assign Program ===== */
function AssignProgramSheet({ isOpen, onClose, athlete, programs, onAssign }) {
  useScrollLock(isOpen);
  const { contentRef, sheetStyle, overlayOpacity, handleTouchStart: onTS, handleTouchMove: onTM, handleTouchEnd: onTE, handleClose } = useSheetDrag(onClose);
  const [selectedProgramId, setSelectedProgramId] = useState(null);

  if (!isOpen || !athlete) return null;

  const hasActiveProgram = !!athlete.program;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedProgramId) {
      onAssign(selectedProgramId);
      setSelectedProgramId(null);
    }
  };

  return (
    <div className="cd-sheet-overlay" style={{ backgroundColor: `rgba(0,0,0,${overlayOpacity})` }} onClick={handleClose}>
      <div ref={contentRef} className="cd-sheet" style={sheetStyle} onClick={(e) => e.stopPropagation()}
           onTouchStart={onTS} onTouchMove={onTM} onTouchEnd={onTE}>
        <div className="cd-sheet-handle" />
        <div className="cd-sheet-header">
          <h2 className="cd-sheet-title">
            {hasActiveProgram ? 'Sostituisci' : 'Assegna'} Scheda
          </h2>
          <button className="cd-sheet-close" onClick={handleClose}>✕</button>
        </div>
        <div className="cd-sheet-body">
          {hasActiveProgram && (
            <div className="cd-assign-warning">
              Scheda attiva: <strong>{athlete.program}</strong>. La nuova scheda sostituirà quella attuale.
            </div>
          )}

          {programs.length === 0 ? (
            <EmptyState
              icon="📋"
              title="Nessun programma disponibile"
              description="Devi creare prima un programma dalla tab Programmi prima di poterlo assegnare."
            />
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="cd-radio-list">
                {programs.map(program => (
                  <label
                    key={program.id}
                    className={`cd-radio-item ${selectedProgramId === program.id ? 'cd-radio-item--selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="program"
                      value={program.id}
                      checked={selectedProgramId === program.id}
                      onChange={() => setSelectedProgramId(program.id)}
                    />
                    <div>
                      <div className="cd-radio-name">{program.name}</div>
                      {program.description && (
                        <div className="cd-radio-desc">{program.description}</div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
              <div className="cd-sheet-actions" style={{ marginTop: '16px' }}>
                <button type="button" className="cd-sheet-btn-cancel" onClick={handleClose}>
                  Annulla
                </button>
                <button
                  type="submit"
                  className="cd-sheet-btn-submit"
                  disabled={!selectedProgramId}
                >
                  {hasActiveProgram ? 'Sostituisci' : 'Assegna'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

/* ===== Bottom Sheet: Program Actions ===== */
function ProgramActionsSheet({ isOpen, program, loading, onClose, onEdit, onDuplicate, onRename, onDelete }) {
  useScrollLock(isOpen);
  const { contentRef, sheetStyle, overlayOpacity, handleTouchStart: onTS, handleTouchMove: onTM, handleTouchEnd: onTE, handleClose } = useSheetDrag(onClose);

  if (!isOpen || !program) return null;

  return (
    <div className="cd-sheet-overlay" style={{ backgroundColor: `rgba(0,0,0,${overlayOpacity})` }} onClick={handleClose}>
      <div ref={contentRef} className="cd-sheet cd-sheet--compact" style={sheetStyle} onClick={(e) => e.stopPropagation()}
           onTouchStart={onTS} onTouchMove={onTM} onTouchEnd={onTE}>
        <div className="cd-sheet-handle" />
        <div className="cd-sheet-header">
          <h2 className="cd-sheet-title">{program.name}</h2>
          <button className="cd-sheet-close" onClick={handleClose}>✕</button>
        </div>
        <div className="cd-sheet-body cd-action-list">
          <button className="cd-action-item" onClick={onEdit} disabled={loading}>
            <span className="cd-action-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </span>
            <span>Modifica</span>
          </button>
          <button className="cd-action-item" onClick={onDuplicate} disabled={loading}>
            <span className="cd-action-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
            </span>
            <span>{loading ? 'Duplicazione...' : 'Duplica'}</span>
          </button>
          <button className="cd-action-item" onClick={onRename} disabled={loading}>
            <span className="cd-action-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
              </svg>
            </span>
            <span>Rinomina</span>
          </button>
          <button className="cd-action-item cd-action-item--danger" onClick={onDelete} disabled={loading}>
            <span className="cd-action-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </span>
            <span>Elimina</span>
          </button>
        </div>
      </div>
    </div>
  );
}

/* ===== Bottom Sheet: Rename Program ===== */
function RenameProgramSheet({ isOpen, program, loading, onClose, onRename }) {
  useScrollLock(isOpen);
  const { contentRef, sheetStyle, overlayOpacity, handleTouchStart: onTS, handleTouchMove: onTM, handleTouchEnd: onTE, handleClose } = useSheetDrag(onClose);
  const [name, setName] = useState('');

  useEffect(() => {
    if (program) {
      setName(program.name);
    }
  }, [program]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (name.trim() && program) {
      onRename(program.id, name.trim());
    }
  };

  if (!isOpen || !program) return null;

  return (
    <div className="cd-sheet-overlay" style={{ backgroundColor: `rgba(0,0,0,${overlayOpacity})` }} onClick={handleClose}>
      <div ref={contentRef} className="cd-sheet cd-sheet--compact" style={sheetStyle} onClick={(e) => e.stopPropagation()}
           onTouchStart={onTS} onTouchMove={onTM} onTouchEnd={onTE}>
        <div className="cd-sheet-handle" />
        <div className="cd-sheet-header">
          <h2 className="cd-sheet-title">Rinomina Programma</h2>
          <button className="cd-sheet-close" onClick={handleClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit} className="cd-sheet-body">
          <div className="cd-field">
            <label>Nome</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nome programma"
              required
              autoFocus
              disabled={loading}
            />
          </div>
          <div className="cd-sheet-actions">
            <button type="button" className="cd-sheet-btn-cancel" onClick={handleClose} disabled={loading}>
              Annulla
            </button>
            <button
              type="submit"
              className="cd-sheet-btn-submit"
              disabled={loading || !name.trim() || name.trim() === program.name}
            >
              {loading ? 'Salvataggio...' : 'Salva'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ===== Bottom Sheet: Delete Confirmation ===== */
function DeleteConfirmSheet({ program, activeCount, loading, onClose, onConfirm }) {
  useScrollLock(true);
  const { contentRef, sheetStyle, overlayOpacity, handleTouchStart: onTS, handleTouchMove: onTM, handleTouchEnd: onTE, handleClose } = useSheetDrag(onClose);

  return (
    <div className="cd-sheet-overlay" style={{ backgroundColor: `rgba(0,0,0,${overlayOpacity})` }} onClick={handleClose}>
      <div ref={contentRef} className="cd-sheet cd-sheet--compact" style={sheetStyle} onClick={(e) => e.stopPropagation()}
           onTouchStart={onTS} onTouchMove={onTM} onTouchEnd={onTE}>
        <div className="cd-sheet-handle" />
        <div className="cd-sheet-header">
          <h2 className="cd-sheet-title">Elimina Programma</h2>
          <button className="cd-sheet-close" onClick={handleClose}>✕</button>
        </div>
        <div className="cd-sheet-body">
          <p className="cd-delete-msg">
            Vuoi eliminare <strong>{program.name}</strong>?
          </p>
          {activeCount > 0 && (
            <div className="cd-assign-warning">
              Questo programma è attivo per <strong>{activeCount}</strong> atleta/i. Verrà disassegnato automaticamente.
            </div>
          )}
          <div className="cd-sheet-actions">
            <button className="cd-sheet-btn-cancel" onClick={handleClose} disabled={loading}>
              Annulla
            </button>
            <button className="cd-sheet-btn-danger" onClick={onConfirm} disabled={loading}>
              {loading ? 'Eliminazione...' : 'Elimina'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CoachDashboard;
